import React, { useEffect, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { frontloadConnect } from 'react-frontload';
import _ from 'lodash';
// eslint-disable-next-line no-restricted-imports
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { categoryUrlSelector } from '../../../modules/selectors/url';

import Meisterkachel from '../../components/Meisterkachel/Meisterkachel';
import PriceRangeFilter from '../../components/Filter/PriceRangeFilter/PriceRangeFilter';
import ProductList from '../../components/product-list';
import ProductListFooter from '../../components/product-list/footer';
import ReduxSuspense from '../../components/ReduxSuspense/ReduxSuspense';
import CheckboxGroup from '../../components/form-elements/CheckboxGroup';
import FilterLayout from '../../components/Filter/FilterLayout';

import { fetchProductsForCategoryIfNeeded, setProductsFilterValues } from '../../../modules/actions/standort/products';
import {
    productsFilterOptionsSelector,
    productsFilterValuesSelector,
    productsResponseSelector,
    productsSelector,
} from '../../../modules/selectors/standort/vehicleFilter/products';
import { parseSearch } from '../../../helpers/UrlHelper';
import OptionsSidebar from '../../components/OptionsSidebar/OptionsSidebar';
import BreadCrumbSelector from '../../components/BreadCrumb/BreadCrumbSelector';
import VehicleFilter from '../../components/Filter/VehicleFilter/VehicleFilter';
import { markenStandortHasDatSelector, markenstandortHasFahrzeugSpezifischeSucheSelector } from '../../../modules/selectors/standort/markenStandort';
import {
    vehicleDataSelector
} from '../../../modules/selectors/vehicle';
import { standortUrlSelector } from '../../../modules/selectors/url';
import { currentHostIsShopSystemSelector } from '../../../modules/selectors/hosts';
import { categoriesSelector } from '../../../modules/selectors/standort/categories';
import { getBaseCrumbs } from '../../components/BreadCrumb/breadCrumbHelper';
import { seoMetaDataResponseSelector } from '../../../modules/selectors/standort/seoMetaData';

const FilterBoxen = ({ options = {}, selectedOptions = {}, onFilterChanged }) => {

    const hasDat = useSelector(markenStandortHasDatSelector);
    const hasFSS = useSelector(markenstandortHasFahrzeugSpezifischeSucheSelector);

    const expandFabrikat = selectedOptions.fabrikate.length > 0 ? true : false;

    return (
        <>
            {hasDat && hasFSS && <VehicleFilter />}
            {options.hersteller && options.hersteller.length > 0 && (
                <FilterLayout textHeader="Hersteller">
                    <CheckboxGroup
                        name="hersteller"
                        options={options.hersteller.map(option => ({ label: option, value: option }))}
                        checkedValues={selectedOptions.hersteller}
                        onChange={({ name, values }) => onFilterChanged({ name, value: values })}
                        ariaLabel="Hersteller"
                    />
                </FilterLayout>
            )}
            {options.articleModel && options.articleModel.length > 0 && (
                <FilterLayout textHeader="Modell">
                    <CheckboxGroup
                        name="articleModel"
                        options={options.articleModel.map(option => ({ label: option, value: option }))}
                        checkedValues={selectedOptions.articleModel}
                        onChange={({ name, values }) => onFilterChanged({ name, value: values })}
                        ariaLabel="Modell"
                    />
                </FilterLayout>
            )}
            {options.warengruppen && options.warengruppen.length > 0 && (
                <FilterLayout textHeader="Warengruppe">
                    <CheckboxGroup
                        name="warengruppen"
                        options={options.warengruppen.map(option => ({ label: option, value: option }))}
                        checkedValues={selectedOptions.warengruppen}
                        onChange={({ name, values }) => onFilterChanged({ name, value: values })}
                        ariaLabel="Warengruppe"
                    />
                </FilterLayout>
            )}
            {options.preisspanne && options.preisspanne.minPrice < options.preisspanne.maxPrice && (
                <FilterLayout textHeader="Preis">
                    <PriceRangeFilter
                        name="preis"
                        currentMinPrice={selectedOptions.preisspanne.minPrice}
                        currentMaxPrice={selectedOptions.preisspanne.maxPrice}
                        minPrice={options.preisspanne.minPrice || 0}
                        maxPrice={options.preisspanne.maxPrice || 0}
                        onFilterChanged={value => onFilterChanged({ name: 'preisspanne', value })}
                    />
                </FilterLayout>
            )}
            {options.fabrikate && options.fabrikate.length > 0 && (
                <FilterLayout textHeader="Fabrikat" open={expandFabrikat}>
                    <CheckboxGroup
                        name="fabrikate"
                        options={options.fabrikate.map(option => ({ label: option, value: option }))}
                        checkedValues={selectedOptions.fabrikate}
                        onChange={({ name, values }) => onFilterChanged({ name, value: values })}
                        ariaLabel="Fabrikat"
                    />
                </FilterLayout>
            )}
        </>
    );
};

function Products({ productsResponse }) {
    const location = useLocation();
    const dispatch = useDispatch();
    const products = useSelector(productsSelector);
    const options = useSelector(productsFilterOptionsSelector);
    const productsFilterValues = useSelector(productsFilterValuesSelector);
    const vehicleData = useSelector(vehicleDataSelector);
    const history = useHistory();
    const categoryUrl = useSelector(categoryUrlSelector);
    const categories = useSelector(categoriesSelector);
    const { filters } = useParams();
    const standortUrl = useSelector(standortUrlSelector);
    const ishost = useSelector(currentHostIsShopSystemSelector);
    const seoMetaDataResponse = useSelector(seoMetaDataResponseSelector);

    const crumbs = useMemo(() => {
        const crumbs = getBaseCrumbs({ standortUrl, categories, categoryUrl });
        return [...crumbs];
    }, [standortUrl, categories, categoryUrl]);


    const queryFilterValues = useMemo(() => {
        let { warengruppen, warengruppe, preisspanne, fabrikate, fabrikat, hersteller, articleModel } = parseSearch(location.search);
        fabrikate = fabrikate ?? fabrikat;  
        warengruppen = warengruppen ?? warengruppe;

        return {
            hersteller: hersteller ? _.castArray(hersteller) : [],
            articleModel: articleModel ? _.castArray(articleModel) : [],
            fabrikate: fabrikate ? _.castArray(fabrikate) : [],
            warengruppen: warengruppen ? _.castArray(warengruppen) : [],
            preisspanne: {
                minPrice: preisspanne?.minPrice ?? options?.preisspanne?.minPrice,
                maxPrice: preisspanne?.maxPrice ?? options?.preisspanne?.maxPrice,
            },
        };
    }, [options, location.search]);


    const filteredURLValues = useMemo(() => {

        let { ...queryParams } = parseSearch(location.search);

        if (!filters && !queryParams) return null;

        let filterSegments = filters ? filters.split('_') : [];

        let hersteller = [];
        let warengruppen = [];
        let articleModel = [];
        let fabrikate = [];
        let preisspanne = { minPrice: options?.preisspanne?.minPrice, maxPrice: options?.preisspanne?.maxPrice };

        const formatValue = (key, value) => {
            const formattedValue = value;
            if (options && options[key]) {
                return options[key].find(item => {
                    let umlautReplaced = item
                        .replace(/ä/g, 'ae')
                        .replace(/ö/g, 'oe')
                        .replace(/ü/g, 'ue')
                        .replace(/ß/g, 'ss')
                        .replace(/Ä/g, 'Ae')
                        .replace(/Ö/g, 'Oe')
                        .replace(/Ü/g, 'Ue');

                    return umlautReplaced.replace(/[.\s/()-]+/g, '-').replace(/-+$/, '').toLowerCase() === formattedValue
                }) || formattedValue;
            }
            return formattedValue;
        };

        if (filterSegments.length > 0) {
            const priceIndex = filterSegments.findIndex(seg => /^price-\d+-\d+$/.test(seg));
            if (priceIndex !== -1) {
                const [_, min, max] = filterSegments[priceIndex].match(/^price-(\d+)-(\d+)$/);
                preisspanne.minPrice = min;
                preisspanne.maxPrice = max;
                filterSegments.splice(priceIndex, 1);
            } 

            filterSegments.forEach(segment => {
                const items = segment.split('.');
                items.forEach(item => {
                    const formatted = formatValue('hersteller', item);
                    if (options?.hersteller?.includes(formatted)) {
                        hersteller.push(formatted);
                        return;
                    }

                    const formattedWg = formatValue('warengruppen', item);
                    if (options?.warengruppen?.includes(formattedWg)) {
                        warengruppen.push(formattedWg);
                        return;
                    }

                    const formattedModel = formatValue('articleModel', item);
                    if (options?.articleModel?.includes(formattedModel)) {
                        articleModel.push(formattedModel);
                        return;
                    }
                    const formattedFabrikat = formatValue('fabrikate', item);
                    if (options?.fabrikate?.includes(formattedFabrikat)) {
                        fabrikate.push(formattedFabrikat);
                        return;
                    }
                });
            });
        }

        let parsedFilters = Object.keys(queryParams).reduce((acc, key) => {
            const valueString = queryParams[key];

            const valueArray = typeof valueString === 'string' ? valueString.split(',') : [valueString];

            acc[key] = valueArray.map(item => formatValue(key, item.trim()));
            return acc;
        }, {});

        return {
            hersteller,
            warengruppen,
            fabrikate,
            articleModel,
            preisspanne,
            queryParams: { ...queryParams, ...parsedFilters }
        };

    }, [filters, options, location.search]);

    useEffect(() => {
        if (productsFilterValues) {
            const newUrl = createSpeakingUrl(productsFilterValues);
            const currentUrl = location.pathname + location.search;

            if (newUrl && newUrl !== currentUrl) {
                history.replace(newUrl);
            } 
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, productsFilterValues, location.pathname, location.search])

    const selectedOptions = productsFilterValues ?? filteredURLValues ?? queryFilterValues; 
  

    const onFilterChanged = ({ name, value }) => {
        const updatedOptions = { ...selectedOptions, [name]: value };
        dispatch(setProductsFilterValues(updatedOptions));

        const newUrl = createSpeakingUrl(updatedOptions);

        const currentUrl = location.pathname + location.search;

        if (newUrl && newUrl !== currentUrl) {
            history.replace(newUrl);
        }
    };

    const createSpeakingUrl = (newOptions) => {
        let herstellerSegment = '';
        let warrengruppeSegment = '';
        let modellSegment = '';
        let fabrikateSegment = '';
        let urlSegments = [];

        if (newOptions.hersteller && Array.isArray(newOptions.hersteller)) {
            herstellerSegment = newOptions.hersteller
                .map(item => {
                    let umlautReplaced = item
                        .replace(/ä/g, 'ae')
                        .replace(/ö/g, 'oe')
                        .replace(/ü/g, 'ue')
                        .replace(/ß/g, 'ss')
                        .replace(/Ä/g, 'Ae')
                        .replace(/Ö/g, 'Oe')
                        .replace(/Ü/g, 'Ue');

                    return umlautReplaced
                        .replace(/[.\s/()-]+/g, '-')
                        .replace(/-+$/, '')
                        .toLowerCase();
                })
                .join('.');
        }

        if (newOptions.warengruppen && Array.isArray(newOptions.warengruppen)) {
            warrengruppeSegment = newOptions.warengruppen
                .map(item => {
                    let umlautReplaced = item
                        .replace(/ä/g, 'ae')
                        .replace(/ö/g, 'oe')
                        .replace(/ü/g, 'ue')
                        .replace(/ß/g, 'ss')
                        .replace(/Ä/g, 'Ae')
                        .replace(/Ö/g, 'Oe')
                        .replace(/Ü/g, 'Ue');

                    return umlautReplaced
                        .replace(/[.\s/()-]+/g, '-')
                        .replace(/-+$/, '')
                        .toLowerCase();
                })
                .join('.');
        }

        if (newOptions.articleModel && Array.isArray(newOptions.articleModel)) {
            modellSegment = newOptions.articleModel
                .map(item => {
                    let umlautReplaced = item
                        .replace(/ä/g, 'ae')
                        .replace(/ö/g, 'oe')
                        .replace(/ü/g, 'ue')
                        .replace(/ß/g, 'ss')
                        .replace(/Ä/g, 'Ae')
                        .replace(/Ö/g, 'Oe')
                        .replace(/Ü/g, 'Ue');

                    return umlautReplaced
                        .replace(/[.\s/()-]+/g, '-')
                        .replace(/-+$/, '')
                        .toLowerCase();
                })
                .join('.');
        }

        if (newOptions.fabrikate && Array.isArray(newOptions.fabrikate)) {
            fabrikateSegment = newOptions.fabrikate
                .map(item => {
                    let umlautReplaced = item
                        .replace(/ä/g, 'ae')
                        .replace(/ö/g, 'oe')
                        .replace(/ü/g, 'ue')
                        .replace(/ß/g, 'ss')
                        .replace(/Ä/g, 'Ae')
                        .replace(/Ö/g, 'Oe')
                        .replace(/Ü/g, 'Ue');

                    return umlautReplaced
                        .replace(/[.\s/()-]+/g, '-')
                        .replace(/-+$/, '')
                        .toLowerCase();
                })
                .join('.');
        }

        const parts = [
            herstellerSegment,
            warrengruppeSegment,
            modellSegment,
            fabrikateSegment
        ].filter(Boolean);

        if (parts.length > 0) {
            urlSegments.push(parts.join('_'));
        }

        const hasPriceChanged =
            newOptions.preisspanne?.minPrice !== options.preisspanne?.minPrice ||
            newOptions.preisspanne?.maxPrice !== options.preisspanne?.maxPrice;

        if (hasPriceChanged && (newOptions.preisspanne?.minPrice || newOptions.preisspanne?.maxPrice)) {
            const min = newOptions.preisspanne.minPrice || '0';
            const max = newOptions.preisspanne.maxPrice || '0';
            const priceSegment = `price-${min}-${max}`;

            if (urlSegments.length > 0) {
                urlSegments[urlSegments.length - 1] += `_${priceSegment}`;
            } else {
                urlSegments.push(priceSegment);
            }
        }

        const queryParams = new URLSearchParams();

        Object.keys(newOptions).forEach((key) => {
            const filterValues = newOptions[key];

            if (key !== 'hersteller' && key !== 'warengruppen' && key !== 'preisspanne' && key !== 'articleModel' && key !== 'fabrikate') {
                if (Array.isArray(filterValues) && filterValues.length > 0) {
                    const formattedValues = filterValues.map(item => {
                        let umlautReplaced = item
                            .replace(/ä/g, 'ae')
                            .replace(/ö/g, 'oe')
                            .replace(/ü/g, 'ue')
                            .replace(/ß/g, 'ss')
                            .replace(/Ä/g, 'Ae')
                            .replace(/Ö/g, 'Oe')
                            .replace(/Ü/g, 'Ue');
                        return umlautReplaced.replace(/[.\s/()-]+/g, '-').replace(/-+$/, '').toLowerCase()
                    }
                    ).join(',');

                    queryParams.set(key, formattedValues);
                } else if (typeof filterValues === 'string' && filterValues) {
                    const formattedValue = filterValues.replace(/[.\s/()-]+/g, '-').replace(/-+$/, '').toLowerCase();
                    queryParams.set(key, formattedValue);
                }
            }

        });

        let queryString = null;
        let url = '';

        if (urlSegments.length > 0) {
            if (standortUrl && ishost) {
                url = `/${standortUrl}/${categoryUrl}/filters/${urlSegments.join('_')}`.replace(/\/+/g, '/');
            } else {
                url = `/${categoryUrl}/filters/${urlSegments.join('_')}`.replace(/\/+/g, '/');
            }
        } else {
            if (standortUrl && ishost) {
                url = `/${standortUrl}/${categoryUrl}`;
            } else {
                url = `/${categoryUrl}`;
            }
        }

        if (queryParams.toString()) {
            queryString = queryParams.toString().replace(/%2C/g, ',');
        }

        if (queryString) {
            url += `?${queryString}`;
        }

        return url;
    };

    const filteredProducts = useMemo(() => {
        if (!products) return [];
        return products.filter(product => {
            let valid = true;
            if (selectedOptions?.hersteller?.length > 0 && valid) {
                valid = !!selectedOptions.hersteller.includes(product.hersteller);
            }
            if (selectedOptions?.articleModel?.length > 0 && valid) {
                valid = !!selectedOptions.articleModel.includes(product.articleModel);
            }
            if (product?.fabrikate && selectedOptions?.fabrikate?.length > 0 && valid) {
                valid = selectedOptions.fabrikate.some(f => product.fabrikate.includes(f));
            }
            if (selectedOptions?.warengruppen?.length > 0 && valid) {
                valid = !!selectedOptions.warengruppen.includes(
                    product.warengruppe?.benennung || product.warengruppenText
                );
            }
            if (selectedOptions?.preisspanne?.minPrice >= 0 && valid) {
                valid = selectedOptions.preisspanne.minPrice <= product.preisAb;
            }
            if (selectedOptions?.preisspanne?.maxPrice > 1 && valid) {
                valid = selectedOptions.preisspanne.maxPrice >= product.preisAb;
            }
            if (vehicleData !== '' && valid) {
                valid = (product?.vehicles && product?.vehicles.some(cars => (
                    cars?.vehicleTypeLabel === vehicleData.vehicleTypeLabel
                        && cars?.manufacturerLabel === vehicleData.manufacturerLabel
                        && vehicleData.baseModelLabel ? cars?.baseModelLabel === vehicleData.baseModelLabel : true
                    && (cars?.subModelLabel && vehicleData.subModelLabel && vehicleData.subModelLabel !== "" ? cars?.subModelLabel === vehicleData.subModelLabel : true))
                )) || product?.fabrikate?.includes('Universal');
            }
            return valid;
        });
    }, [products, selectedOptions, vehicleData]);

    return (
        <>
            <BreadCrumbSelector showOptionsSidebarToggle />
            <ReduxSuspense response={productsResponse}>
                {_.isEmpty(products) ? (
                    <section className="uk-section uk-section-muted uk-section-small">
                        <div className="uk-container uk-container-xsmall">
                            <Meisterkachel />
                        </div>
                    </section>
                ) : (
                    <section className="uk-section uk-section-default uk-section-small">
                        <div className="uk-container uk-margin-small-top">
                            <div className="uk-grid uk-grid-small uk-margin-top" data-uk-grid="">
                                <div className="sidebar-container uk-width-1-4@m"></div>
                                <div className="uk-width-expand uk-flex uk-flex-start uk-flex-middle uk-flex-wrap uk-margin-large-bottom uk-margin-small-top mobile_header">
                                    {seoMetaDataResponse.data && seoMetaDataResponse.data.category ? (
                                        <h1 className="uk-margin-remove-bottom">{seoMetaDataResponse.data.category}</h1>
                                    ) : (
                                        <h1 className="uk-margin-remove-bottom">{crumbs[2].label}</h1>
                                    )}
                                    <p className='uk-h1 uk-margin-remove-top'> &nbsp;<strong><span>-</span> {filteredProducts.length === 1 ? `${filteredProducts.length} Ergebnis` : `${filteredProducts.length} Ergebnisse`}</strong></p>
                                </div>
                            </div>
                        </div>
                        <div className="uk-container uk-margin-top">
                            <div className="uk-grid uk-grid-small" data-uk-grid="">
                                <OptionsSidebar>
                                    <FilterBoxen options={options} selectedOptions={selectedOptions} onFilterChanged={onFilterChanged} />
                                </OptionsSidebar>
                                <div className="uk-width-expand">
                                    <ProductList products={filteredProducts} />
                                    {/* <ProductListFooter /> */}
                                </div>
                            </div>
                        </div>
                    </section>
                )}
            </ReduxSuspense>
        </>
    );
}

const frontload = async ({ fetchProductsForCategoryIfNeeded }) => {
    await fetchProductsForCategoryIfNeeded();
};

const mapStateToProps = state => ({
    productsResponse: productsResponseSelector(state),
});

const mapDispatchToProps = { fetchProductsForCategoryIfNeeded };

export default connect(mapStateToProps, mapDispatchToProps)(frontloadConnect(frontload, { onUpdate: true })(Products));
